import { useMemo, useState } from "react";
import {
  RedemptionStatus,
  RewardsPermission,
  formatPointsNumber,
  type IRedemption,
} from "@ticketon/rewards-core";
import {
  useCancelRewardRedemptionAdmin,
  useFulfillRewardRedemption,
  useReverseRewardRedemption,
  useRewardRedemptions,
} from "@ticketon/rewards-graphql";
import { CommonTable, Input, type ICommonTableColumn } from "ui";
import type { IRewardsNavHandlers, IRewardsScreenBaseProps } from "../types/nav";
import { useRewardsAdmin } from "../host/RewardsAdminHost";
import { StatusBadge } from "../shared/StatusBadge";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import { Field } from "../shared/Field";
import { PageHeader } from "../shared/PageHeader";
import { FilterBar } from "../shared/FilterBar";
import { TableCard } from "../shared/TableCard";
import { SearchInput } from "../shared/SearchInput";
import { ALL_OPTION_VALUE, OptionSelect } from "../shared/OptionSelect";
import { RowActionsMenu } from "../shared/RowActionsMenu";
import { useErrorMessage } from "../shared/states";
import { useMutationFeedback } from "../shared/useMutationFeedback";
import { filterValue, filterValues, usePagedFilters } from "../shared/usePagedFilters";
import { enumOptions } from "../util/options";
import { RedemptionDetailDrawer } from "./RedemptionDetailDrawer";

export interface IRedemptionsTableScreenProps extends IRewardsScreenBaseProps, IRewardsNavHandlers {}

type RedemptionAction = "fulfill" | "cancel" | "reverse";

interface IPendingAction {
  redemption: IRedemption;
  action: RedemptionAction;
}

const PAGE_SIZE = 20;

const INITIAL_FILTERS = { search: "", status: ALL_OPTION_VALUE, from: "", to: "" };

/** Statuses an admin may fulfill or cancel (README §2: cancel only PENDING / CONFIRMED). */
const OPEN_STATUSES: readonly RedemptionStatus[] = [RedemptionStatus.PENDING, RedemptionStatus.CONFIRMED];

/** The admin actions a redemption allows in its current status. */
export function availableRedemptionActions(status: RedemptionStatus): RedemptionAction[] {
  if (OPEN_STATUSES.includes(status)) return ["fulfill", "cancel"];
  if (status === RedemptionStatus.FULFILLED) return ["reverse"];
  return [];
}

/** Redemptions table with a detail drawer and fulfill / cancel / reverse actions with reason capture. */
export function RedemptionsTableScreen({ t: tOverride, permissions, onOpenRedemption }: IRedemptionsTableScreenProps) {
  const { t, can, formatDate } = useRewardsAdmin({ t: tOverride, permissions });
  const { filters, appliedFilters, setFilter, clearFilters, hasActiveFilters, page, setPageNumber } =
    usePagedFilters(INITIAL_FILTERS, { debouncedKeys: ["search"], pageSize: PAGE_SIZE });
  const [pending, setPending] = useState<IPendingAction | null>(null);
  const [reason, setReason] = useState("");
  const [openRedemptionId, setOpenRedemptionId] = useState<string | null>(null);

  const filter = useMemo(
    () => ({
      search: filterValue(appliedFilters.search),
      statuses: filterValues(appliedFilters.status),
      from: filterValue(appliedFilters.from),
      to: filterValue(appliedFilters.to),
    }),
    [appliedFilters],
  );

  const { data, isLoading, error } = useRewardRedemptions({ page, filter });
  const errorMessage = useErrorMessage(error, t);
  const fulfill = useFulfillRewardRedemption();
  const cancel = useCancelRewardRedemptionAdmin();
  const reverse = useReverseRewardRedemption();
  const { notifySuccess } = useMutationFeedback();

  const canManage = can(RewardsPermission.REDEMPTIONS_MANAGE);

  const openRedemption = (id: string) => {
    setOpenRedemptionId(id);
    onOpenRedemption?.(id);
  };

  const startAction = (redemption: IRedemption, action: RedemptionAction) => {
    setReason("");
    setPending({ redemption, action });
  };

  const columns: ICommonTableColumn<IRedemption>[] = [
    { key: "createdAt", header: t("rewards.redemption.date"), render: (row) => formatDate(row.createdAt) },
    { key: "item", header: t("rewards.redemption.item"), render: (row) => row.item.name },
    {
      key: "pointsSpent",
      header: t("rewards.redemption.points_spent"),
      render: (row) => formatPointsNumber(row.pointsSpent),
    },
    {
      key: "status",
      header: t("rewards.redemption.status"),
      render: (row) => <StatusBadge status={row.status} t={t} />,
    },
    {
      key: "fulfillmentStatus",
      header: t("rewards.redemption.fulfillment"),
      render: (row) => <StatusBadge status={row.fulfillmentStatus} t={t} />,
    },
  ];

  const rowMenu = (row: IRedemption) => {
    const actions = canManage ? availableRedemptionActions(row.status) : [];
    return (
      <RowActionsMenu
        t={t}
        actions={[
          { key: "view", label: t("rewards.action.view"), onSelect: () => openRedemption(row.id) },
          actions.includes("fulfill") && {
            key: "fulfill",
            label: t("rewards.action.fulfill"),
            onSelect: () => startAction(row, "fulfill"),
          },
          actions.includes("cancel") && {
            key: "cancel",
            label: t("rewards.action.cancel_redemption"),
            destructive: true,
            onSelect: () => startAction(row, "cancel"),
          },
          actions.includes("reverse") && {
            key: "reverse",
            label: t("rewards.action.reverse"),
            destructive: true,
            onSelect: () => startAction(row, "reverse"),
          },
        ]}
      />
    );
  };

  const requiresReason = pending !== null && pending.action !== "fulfill";

  const runPending = async () => {
    if (!pending) return;
    const id = pending.redemption.id;
    const trimmedReason = reason.trim();
    if (pending.action === "fulfill") await fulfill.mutate({ id, note: trimmedReason || undefined });
    else if (pending.action === "cancel") await cancel.mutate({ id, reason: trimmedReason });
    else await reverse.mutate({ id, reason: trimmedReason });
    notifySuccess(`rewards.redemptions.${pending.action}_done`);
  };

  return (
    <div className="space-y-6" data-testid="redemptions-table-screen">
      <PageHeader title={t("rewards.redemptions.title")} />

      <FilterBar t={t} hasActiveFilters={hasActiveFilters} onClear={clearFilters}>
        <SearchInput
          value={filters.search}
          onChange={(value) => setFilter("search", value)}
          placeholder={t("rewards.redemptions.search_placeholder")}
          className="w-64"
          data-testid="redemptions-search"
        />
        <OptionSelect
          value={filters.status}
          onValueChange={(value) => setFilter("status", value)}
          options={enumOptions(RedemptionStatus, "rewards.status", t)}
          allLabel={t("rewards.filter.all_statuses")}
          placeholder={t("rewards.redemption.status")}
          triggerClassName="w-44"
        />
        <Input
          type="date"
          value={filters.from}
          onChange={(event) => setFilter("from", event.target.value)}
          className="w-40"
          aria-label={t("rewards.filter.from")}
        />
        <Input
          type="date"
          value={filters.to}
          onChange={(event) => setFilter("to", event.target.value)}
          className="w-40"
          aria-label={t("rewards.filter.to")}
        />
      </FilterBar>

      <TableCard>
        <CommonTable<IRedemption>
          columns={columns}
          data={data?.items ?? []}
          isLoading={isLoading}
          errorMessage={errorMessage}
          emptyMessage={t("rewards.redemptions.empty")}
          page={page.pageNumber}
          pageSize={PAGE_SIZE}
          total={data?.count ?? 0}
          onPageChange={setPageNumber}
          onRowClick={(row) => openRedemption(row.id)}
          rowMenu={rowMenu}
          getRowId={(row) => row.id}
        />
      </TableCard>

      <RedemptionDetailDrawer
        redemptionId={openRedemptionId}
        open={openRedemptionId !== null}
        onOpenChange={(open) => {
          if (!open) setOpenRedemptionId(null);
        }}
        t={t}
      />

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
        title={pending ? t(`rewards.redemptions.confirm_${pending.action}_title`) : ""}
        description={pending ? t(`rewards.redemptions.confirm_${pending.action}_body`) : undefined}
        confirmVariant={pending?.action === "fulfill" ? "default" : "destructive"}
        confirmDisabled={requiresReason && reason.trim().length === 0}
        onConfirm={runPending}
        t={t}
      >
        <Field
          label={requiresReason ? t("rewards.redemptions.reason") : t("rewards.redemptions.note_optional")}
          required={requiresReason}
          t={t}
        >
          <Input value={reason} onChange={(event) => setReason(event.target.value)} data-testid="redemption-reason" />
        </Field>
      </ConfirmDialog>
    </div>
  );
}
